import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FaBuilding, FaRegSave, FaUser } from 'react-icons/fa';
import { MdAdd, MdArrowBack, MdKeyboardReturn } from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import CustomTypeahead from '../../components/CustomTypeahead';
import {
	Button,
	RequiredMark,
	FormField,
	Section,
	controlClass,
	textareaClass,
} from '../../components/EntityList';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import AccountService from '../../services/account.service';
import BankAccountService from '../../services/bankAccount.service';
import { getError, getUserInfo } from '../../utils';
import { isFacturacionEnabled } from '../../utils/featureFlags';

const ACCOUNT_TYPES = ['Persona', 'Empresa'];
const CURRENCIES = ['USD', 'UYU'];

const emptyForm = {
	type: 'Persona',
	name: '',
	email: '',
	address: '',
	phone: '',
	currency: '',
	folder: '',
	tipoDocumento: '',
	nroDocumento: '',
	observations: '',
	facturacionIva: '',
	facturacionCiudad: '',
	rut: '',
	contactName: '',
	bankAccountUSD: null,
	bankAccountUYU: null,
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'SAVE_REQUEST':
		return { ...state, saving: true, error: '' };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false, error: '' };
	case 'SAVE_FAIL':
		return { ...state, saving: false, error: action.payload };
	default:
		return state;
	}
};

const normalizeBankAccounts = (response) => response.data?.data || response.data || [];

const accountToForm = (account, fallbackType) => ({
	...emptyForm,
	type: account?.type || (ACCOUNT_TYPES.includes(fallbackType) ? fallbackType : emptyForm.type),
	name: account?.name || '',
	email: account?.email || '',
	address: account?.address || '',
	phone: account?.phone || '',
	currency: account?.currency || '',
	folder: account?.folder || '',
	tipoDocumento: account?.tipoDocumento || '',
	nroDocumento: account?.nroDocumento || '',
	observations: account?.observations || '',
	facturacionIva: account?.facturacion?.indicadorFacturacion || '',
	facturacionCiudad: account?.facturacion?.ciudad || '',
	rut: account?.rut || '',
	contactName: account?.mainContact?.name || account?.contactName || account?.name || '',
	bankAccountUSD: account?.bankAccountUSD || null,
	bankAccountUYU: account?.bankAccountUYU || null,
});

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const getBankAccountLabel = (option) => {
	if (!option) {
		return '';
	}
	const owner = option.owner || 'Sin titular';
	const bank = option.bank || 'Sin banco';
	const accountNumber = option.accountNumber || 'Sin numero';
	const currency = option.currency || '';
	return `${owner}: ${bank} - ${accountNumber}${currency ? ` (${currency})` : ''}`;
};

const AccountEditor = ({
	mode = 'create',
	accountId,
	initialData = null,
	onCancel,
	onSaved,
	cancelLabel = 'Cancelar',
	saveLabel,
	onReturn,
	returnLabel,
}) => {
	const userInfo = getUserInfo();
	const isEditing = mode === 'edit';
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	// Ids injected via ?bankAccountUSDId=/?bankAccountUYUId= after a "crear
	// nueva cuenta bancaria" round trip (see buildBankAccountLinkToCreate
	// below / NewBankAccount.jsx). Separate keys per field — rather than one
	// generic id + a field marker — so both can be present in the URL at
	// once and a round trip for one field never clobbers the other's
	// selection. Kept in sync with the actual selection (see
	// syncInjectedParam below) rather than left stale after a manual pick.
	const injectedBankAccountUSDId = searchParams.get('bankAccountUSDId');
	const injectedBankAccountUYUId = searchParams.get('bankAccountUYUId');
	// accountType rides in returnTo (create mode only — edit mode's fetched
	// account always wins below) so the Empresa-only section (and the bank
	// account fields inside it) doesn't vanish on return just because the
	// remount reset form.type to its default.
	const injectedAccountType = searchParams.get('accountType');
	const consumedBankAccountUSDId = useRef(null);
	const consumedBankAccountUYUId = useRef(null);

	// Keep the URL mirroring the current selection for either bank-account
	// field. The param is the only state that survives the remount a create
	// round trip causes, so rewrite it on a manual pick (so that choice
	// survives the next trip) and drop it when the field is cleared —
	// otherwise a stale id from an earlier trip rides along in returnTo and
	// gets re-injected on top of the manual choice.
	const syncInjectedParam = (key, id, consumedRef) => {
		const nextValue = id || null;
		if (searchParams.get(key) === nextValue) return;
		// Pre-mark as consumed so the injection effect doesn't re-fetch a
		// record we already hold in state.
		if (nextValue) consumedRef.current = nextValue;
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (nextValue) next.set(key, nextValue);
			else next.delete(key);
			return next;
		}, { replace: true });
	};
	const [{ loading, saving, error }, dispatch] = useReducer(reducer, {
		loading: isEditing && !initialData,
		saving: false,
		error: '',
	});
	const [form, setForm] = useState(() => accountToForm(initialData, injectedAccountType));
	const [bankAccounts, setBankAccounts] = useState([]);
	const [fieldErrors, setFieldErrors] = useState({});

	useEffect(() => {
		let isCurrent = true;

		const fetchData = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				const requests = [BankAccountService.list()];
				if (isEditing && accountId) {
					requests.unshift(AccountService.getDetails(accountId));
				}
				const responses = await Promise.all(requests);
				if (!isCurrent) {
					return;
				}

				if (isEditing && accountId) {
					const [accountRes, bankAccountsRes] = responses;
					setForm(accountToForm(accountRes.data));
					setBankAccounts(normalizeBankAccounts(bankAccountsRes));
				} else {
					const [bankAccountsRes] = responses;
					setForm(accountToForm(initialData, injectedAccountType));
					setBankAccounts(normalizeBankAccounts(bankAccountsRes));
				}
				dispatch({ type: 'FETCH_SUCCESS' });
			} catch (ex) {
				const message = getError(ex);
				if (isCurrent) {
					dispatch({ type: 'FETCH_FAIL', payload: message });
				}
				toast.error(isEditing ? 'No se pudo obtener cuenta' : 'No se pudieron obtener cuentas bancarias');
			}
		};

		fetchData();
		return () => {
			isCurrent = false;
		};
	}, [accountId, initialData, isEditing, injectedAccountType]);

	const bankAccountsUSD = useMemo(() => bankAccounts.filter((bankAccount) => bankAccount.currency === 'USD'), [bankAccounts]);
	const bankAccountsUYU = useMemo(() => bankAccounts.filter((bankAccount) => bankAccount.currency === 'UYU'), [bankAccounts]);

	const updateField = (field, value) => {
		setForm((prevForm) => ({
			...prevForm,
			[field]: value,
		}));
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	// Auto-select a bank account created via the "crear nueva cuenta
	// bancaria" link (see buildBankAccountLinkToCreate below), which returns
	// here with ?bankAccountUSDId=<id> or ?bankAccountUYUId=<id>. No extra
	// fetch needed — the account remounts on return, so bankAccounts is
	// already refetched fresh by the effect above and will contain the new
	// record.
	useEffect(() => {
		if (!injectedBankAccountUSDId || consumedBankAccountUSDId.current === injectedBankAccountUSDId) return;
		const injectedBankAccount = bankAccounts.find((ba) => ba._id === injectedBankAccountUSDId);
		if (!injectedBankAccount) return;
		updateField('bankAccountUSD', injectedBankAccount);
		consumedBankAccountUSDId.current = injectedBankAccountUSDId;
	}, [bankAccounts, injectedBankAccountUSDId]);

	useEffect(() => {
		if (!injectedBankAccountUYUId || consumedBankAccountUYUId.current === injectedBankAccountUYUId) return;
		const injectedBankAccount = bankAccounts.find((ba) => ba._id === injectedBankAccountUYUId);
		if (!injectedBankAccount) return;
		updateField('bankAccountUYU', injectedBankAccount);
		consumedBankAccountUYUId.current = injectedBankAccountUYUId;
	}, [bankAccounts, injectedBankAccountUYUId]);

	const handleTypeChange = (type) => {
		setForm((prevForm) => ({
			...prevForm,
			type,
			contactName: type === 'Empresa' && !prevForm.contactName ? prevForm.name : prevForm.contactName,
		}));
		setFieldErrors((prevErrors) => {
			if (!prevErrors.type && !prevErrors.rut && !prevErrors.contactName) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors.type;
			delete nextErrors.rut;
			delete nextErrors.contactName;
			return nextErrors;
		});
	};

	const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

	const FIELD_LABELS = {
		type: 'Tipo',
		name: 'Nombre',
		rut: 'RUT',
		contactName: 'Nombre de Contacto',
	};

	const getFieldErrors = () => {
		const errors = {};
		if (!form.type) errors.type = REQUIRED_FIELD_MESSAGE;
		if (!form.name.trim()) errors.name = REQUIRED_FIELD_MESSAGE;
		if (form.type === 'Empresa') {
			if (!form.rut.trim()) errors.rut = REQUIRED_FIELD_MESSAGE;
			if (!form.contactName.trim()) errors.contactName = REQUIRED_FIELD_MESSAGE;
		}
		return errors;
	};

	const buildPayload = () => ({
		type: form.type,
		name: form.name,
		observations: form.observations,
		createdBy: userInfo._id,
		email: form.email,
		phone: form.phone,
		address: form.address,
		folder: form.folder,
		rut: form.type === 'Empresa' ? form.rut : '',
		currency: form.currency,
		tipoDocumento: form.tipoDocumento,
		nroDocumento: form.nroDocumento,
		facturacion: {
			indicadorFacturacion: form.facturacionIva || undefined,
			ciudad: form.facturacionCiudad || undefined,
		},
		...(isEditing ? {} : { contactName: form.contactName || form.name }),
		bankAccountUSD: form.bankAccountUSD?._id || form.bankAccountUSD,
		bankAccountUYU: form.bankAccountUYU?._id || form.bankAccountUYU,
	});

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (loading || saving) {
			return;
		}

		const errors = getFieldErrors();
		const missingFields = Object.keys(errors);
		if (missingFields.length > 0) {
			setFieldErrors(errors);
			const missingLabels = missingFields.map((field) => FIELD_LABELS[field] || field);
			dispatch({
				type: 'SAVE_FAIL',
				payload: `Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`,
			});
			return;
		}
		setFieldErrors({});

		dispatch({ type: 'SAVE_REQUEST' });
		try {
			const payload = buildPayload();
			let savedAccountId = accountId;
			if (isEditing) {
				const result = await AccountService.edit(accountId || initialData?._id, payload);
				savedAccountId = result.data?.account?._id || accountId || initialData?._id;
				toast.success('Cuenta actualizada Correctamente');
			} else {
				const result = await AccountService.create(payload);
				savedAccountId = result.data?.account?._id;
				toast.success('Cuenta creada');
			}
			dispatch({ type: 'SAVE_SUCCESS' });
			if (onSaved) {
				onSaved(savedAccountId);
			}
		} catch (ex) {
			const message = getError(ex);
			dispatch({ type: 'SAVE_FAIL', payload: message });
			console.error(message);
			toast.error(isEditing ? 'Error al actualizar cuenta' : 'Error al crear cuenta');
		}
	};

	if (loading) {
		return <DetailPageLoader entityLabel='editor' />;
	}

	const resolvedSaveLabel = saveLabel || (isEditing ? 'Guardar' : 'Agregar');

	// accountType rides inside returnTo itself (not a sibling param on the
	// newBankAccount link) so it survives the round trip — see the comment on
	// injectedAccountType above. currency and bankAccountIdKey, in contrast,
	// are read directly by NewBankAccount.jsx on this link, not forwarded
	// back: currency pre-fills/locks the new record's currency, and
	// bankAccountIdKey tells NewBankAccount.jsx which query key to write the
	// created id onto when it builds the return URL (bankAccountUSDId vs
	// bankAccountUYUId) — field is already exactly 'USD'/'UYU', so it doubles
	// as the currency value with no extra mapping.
	const buildBankAccountLinkToCreate = (field) => {
		const idParamKey = field === 'USD' ? 'bankAccountUSDId' : 'bankAccountUYUId';
		const params = new URLSearchParams(location.search);
		params.set('accountType', form.type);
		const returnTo = `${location.pathname}?${params.toString()}`;
		return `/AdminScreen/newBankAccount?returnTo=${encodeURIComponent(returnTo)}&currency=${field}&bankAccountIdKey=${idParamKey}`;
	};

	return (
		<Form id='account-editor-form' onSubmit={handleSubmit} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEditing ? 'Actualizá los datos de la cuenta.' : 'Completá los datos para crear la cuenta.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						{cancelLabel}
					</Button>
					{onReturn && (
						<Button icon={<MdKeyboardReturn />} onClick={onReturn} disabled={saving}>
							{returnLabel || 'Volver'}
						</Button>
					)}
					<Button variant='primary' icon={isEditing ? <FaRegSave /> : <MdAdd />} type='submit' disabled={saving || loading}>
						{saving ? 'Guardando...' : resolvedSaveLabel}
					</Button>
				</div>
			</div>

			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			<Section title='Tipo de Cuenta' description='Elegí si la cuenta corresponde a una persona o a una empresa.'>
				<div className='tw:inline-flex tw:overflow-hidden tw:rounded-[7px] tw:border tw:border-border-strong tw:bg-surface'>
					{ACCOUNT_TYPES.map((type) => {
						const selected = form.type === type;
						return (
							<button
								key={type}
								type='button'
								className={`tw:inline-flex tw:h-[34px] tw:items-center tw:gap-2 tw:border-0 tw:px-3 tw:text-[13px] tw:font-medium tw:transition-colors ${
									selected ? 'tw:bg-accent tw:text-white' : 'tw:bg-surface tw:text-ink-2 hover:tw:bg-surface-2'
								}`}
								onClick={() => handleTypeChange(type)}
							>
								{type === 'Empresa' ? <FaBuilding /> : <FaUser />}
								{type}
								<RequiredMark />
							</button>
						);
					})}
				</div>
				<FieldError message={fieldErrors.type} />
			</Section>

			<Section title='Datos generales'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='account-name' label={form.type === 'Empresa' ? 'Nombre de Cuenta' : 'Nombre'} required>
						<Form.Control
							id='account-name'
							className={controlClass}
							value={form.name}
							onChange={(event) => updateField('name', event.target.value)}
						/>
						<FieldError message={fieldErrors.name} />
					</FormField>
					<FormField id='account-currency' label='Moneda'>
						<Form.Select
							id='account-currency'
							className={controlClass}
							value={form.currency}
							onChange={(event) => updateField('currency', event.target.value)}
						>
							<option value=''>Elegí Moneda</option>
							{CURRENCIES.map((currency) => (
								<option key={currency} value={currency}>
									{currency}
								</option>
							))}
						</Form.Select>
					</FormField>
					<FormField id='account-folder' label='Carpeta'>
						<Form.Control
							id='account-folder'
							className={controlClass}
							value={form.folder}
							onChange={(event) => updateField('folder', event.target.value)}
						/>
					</FormField>
					<FormField id='account-document-type' label='Tipo de documento'>
						<Form.Control
							id='account-document-type'
							className={controlClass}
							value={form.tipoDocumento}
							onChange={(event) => updateField('tipoDocumento', event.target.value)}
						/>
					</FormField>
					<FormField id='account-document-number' label='Nro. de documento'>
						<Form.Control
							id='account-document-number'
							className={controlClass}
							value={form.nroDocumento}
							onChange={(event) => updateField('nroDocumento', event.target.value)}
						/>
					</FormField>
					{isFacturacionEnabled() && (
						<>
							<FormField id='account-facturacion-iva' label='IVA para facturación'>
								<Form.Select
									id='account-facturacion-iva'
									className={controlClass}
									value={form.facturacionIva}
									onChange={(event) => updateField('facturacionIva', event.target.value)}
								>
									<option value=''>Usar predeterminado de la empresa</option>
									<option value='basica'>IVA básica (22%)</option>
									<option value='minima'>IVA mínima (10%)</option>
									<option value='exento'>Exento de IVA</option>
								</Form.Select>
							</FormField>
							<FormField id='account-facturacion-ciudad' label='Ciudad (receptor CFE)'>
								<Form.Control
									id='account-facturacion-ciudad'
									className={controlClass}
									value={form.facturacionCiudad}
									onChange={(event) => updateField('facturacionCiudad', event.target.value)}
								/>
							</FormField>
						</>
					)}
				</div>
			</Section>

			<Section title='Contacto'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='account-email' label='Email'>
						<Form.Control
							id='account-email'
							type='email'
							className={controlClass}
							value={form.email}
							onChange={(event) => updateField('email', event.target.value)}
						/>
					</FormField>
					<FormField id='account-phone' label='Teléfono'>
						<Form.Control
							id='account-phone'
							className={controlClass}
							value={form.phone}
							onChange={(event) => updateField('phone', event.target.value)}
						/>
					</FormField>
					<FormField id='account-address' label='Dirección' className='md:tw:col-span-2'>
						<Form.Control
							id='account-address'
							className={controlClass}
							value={form.address}
							onChange={(event) => updateField('address', event.target.value)}
						/>
					</FormField>
				</div>
			</Section>

			{form.type === 'Empresa' && (
				<Section title='Empresa' description='Datos fiscales y cuentas bancarias asociadas.'>
					<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
						<FormField id='account-rut' label='RUT' required>
							<Form.Control
								id='account-rut'
								className={`${controlClass} tw:font-mono`}
								value={form.rut}
								onChange={(event) => updateField('rut', event.target.value)}
							/>
							<FieldError message={fieldErrors.rut} />
						</FormField>
						<FormField id='account-contact-name' label='Nombre de Contacto' required>
							<Form.Control
								id='account-contact-name'
								className={controlClass}
								value={form.contactName}
								onChange={(event) => updateField('contactName', event.target.value)}
							/>
							<FieldError message={fieldErrors.contactName} />
						</FormField>
						<FormField id='account-bank-usd' label='Cuenta Bancaria USD'>
							<CustomTypeahead
								id='account-bank-usd'
								labelKey={getBankAccountLabel}
								onChange={(selected) => {
									const nextBankAccount = selected && selected.length > 0 ? selected[0] : null;
									syncInjectedParam('bankAccountUSDId', nextBankAccount?._id, consumedBankAccountUSDId);
									updateField('bankAccountUSD', nextBankAccount);
								}}
								options={bankAccountsUSD}
								selected={form.bankAccountUSD ? [form.bankAccountUSD] : []}
								placeholder='Buscar o crear cuenta bancaria USD...'
								linkToCreate={buildBankAccountLinkToCreate('USD')}
								newOption='nueva cuenta bancaria'
								disabled={saving}
								variant='entity'
							/>
						</FormField>
						<FormField id='account-bank-uyu' label='Cuenta Bancaria UYU'>
							<CustomTypeahead
								id='account-bank-uyu'
								labelKey={getBankAccountLabel}
								onChange={(selected) => {
									const nextBankAccount = selected && selected.length > 0 ? selected[0] : null;
									syncInjectedParam('bankAccountUYUId', nextBankAccount?._id, consumedBankAccountUYUId);
									updateField('bankAccountUYU', nextBankAccount);
								}}
								options={bankAccountsUYU}
								selected={form.bankAccountUYU ? [form.bankAccountUYU] : []}
								placeholder='Buscar o crear cuenta bancaria UYU...'
								linkToCreate={buildBankAccountLinkToCreate('UYU')}
								newOption='nueva cuenta bancaria'
								disabled={saving}
								variant='entity'
							/>
						</FormField>
					</div>
				</Section>
			)}

			<Section title='Observaciones' description='Notas internas o información adicional de la cuenta.'>
				<FormField id='account-observations' label='Observaciones'>
					<Form.Control
						id='account-observations'
						as='textarea'
						rows={8}
						className={textareaClass}
						value={form.observations}
						onChange={(event) => updateField('observations', event.target.value)}
					/>
				</FormField>
			</Section>
		</Form>
	);
};

AccountEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	accountId: PropTypes.string,
	initialData: PropTypes.object,
	onCancel: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	cancelLabel: PropTypes.string,
	saveLabel: PropTypes.string,
	onReturn: PropTypes.func,
	returnLabel: PropTypes.string,
};

export default AccountEditor;
